import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { Participant, ParticipantStatus } from '../../../../types';
import { participantsApi, type ParticipantSummary, type AssignedStatus, type CohortCustomFieldDefinition } from '../../../../services/api/participants';
import { groupsApi, type GroupWithMembers, type GroupsPagination } from '../../../../services/api/groups';
import { feedbackSurveysApi } from '../../../../services/api/feedbackSurveys';
import { useColumnConfig, type SortConfig } from '../../../../hooks';
import { DEFAULT_COLUMNS } from './participantColumnConfig';
import type { FormBaseUrls } from './ParticipantCellRenderers';

export type IntakeFilter = 'all' | 'not_sent' | 'pending' | 'completed';

export interface UseParticipantsDataOptions {
  cohortId: string;
  isActive?: boolean;
  viewMode: 'participants' | 'groups';
  groupsDisplayMode?: 'list' | 'card';
  groupsSearchValue?: string;
}

const GROUPS_PAGE_SIZE = 10;

export interface ParticipantsFilterState {
  searchValue: string;
  setSearchValue: (value: string) => void;
  debouncedSearch: string;
  statusFilter: ParticipantStatus | 'all';
  setStatusFilter: (value: ParticipantStatus | 'all') => void;
  groupFilter: string;
  setGroupFilter: (value: string) => void;
  assignedFilter: AssignedStatus;
  setAssignedFilter: (value: AssignedStatus) => void;
  personalityFilter: string;
  setPersonalityFilter: (value: string) => void;
  intakeFilter: IntakeFilter;
  setIntakeFilter: (value: IntakeFilter) => void;
  flaggedOnly: boolean;
  setFlaggedOnly: (value: boolean) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export interface UseParticipantsDataReturn {
  // Data
  participants: Participant[];
  filteredParticipants: Participant[];
  summary: ParticipantSummary | null;
  displaySummary: { total: number; unassigned: number; avgAttendance: number | null };
  totalCount: number;
  totalPages: number;
  loading: boolean;
  /** True when refetching after initial load (use for overlay instead of full loader) */
  refetching: boolean;
  customFieldDefinitions: CohortCustomFieldDefinition[];
  // Groups data
  cohortGroups: GroupWithMembers[];
  loadingGroups: boolean;
  loadingMoreGroups: boolean;
  groupsPagination: GroupsPagination | null;
  loadMoreGroups: () => Promise<void>;
  allParticipantsForGroups: Participant[];
  // Unique filter options derived from data
  groupOptions: { value: string; label: string }[];
  personalityTypes: string[];
  // Filter state
  filters: ParticipantsFilterState;
  // Column config (from useColumnConfig hook)
  columnConfig: ReturnType<typeof useColumnConfig>;
  // Sort config (shortcut)
  sortConfig: SortConfig | null;
  // Pagination
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  // Form URLs
  formBaseUrls: FormBaseUrls;
  // Actions
  fetchParticipants: () => Promise<void>;
  fetchGroups: () => Promise<void>;
  fetchAllParticipantsForGroups: () => Promise<void>;
}

export function useParticipantsData({ cohortId, isActive, viewMode, groupsDisplayMode = 'list', groupsSearchValue }: UseParticipantsDataOptions): UseParticipantsDataReturn {
  // Core data state
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [summary, setSummary] = useState<ParticipantSummary | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CohortCustomFieldDefinition[]>([]);

  // Form base URLs for feedback surveys
  const [formBaseUrls, setFormBaseUrls] = useState<FormBaseUrls>({ midpoint: null, endpoint: null, post_session: null });

  // Groups data
  const [cohortGroups, setCohortGroups] = useState<GroupWithMembers[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
  const [groupsPagination, setGroupsPagination] = useState<GroupsPagination | null>(null);
  const [allParticipantsForGroups, setAllParticipantsForGroups] = useState<Participant[]>([]);

  // Filter state
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [statusFilter, setStatusFilter] = useState<ParticipantStatus | 'all'>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [assignedFilter, setAssignedFilter] = useState<AssignedStatus>('all');
  const [personalityFilter, setPersonalityFilter] = useState<string>('all');
  const [intakeFilter, setIntakeFilter] = useState<IntakeFilter>('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  // Handle custom field deletion
  const handleDeleteCustomFieldApi = useCallback(async (fieldId: string) => {
    await participantsApi.deleteCustomField(cohortId, fieldId);
    setCustomFieldDefinitions((current) => current.filter((cf) => cf.id !== fieldId));
    notifications.show({
      title: 'Custom Column Deleted',
      message: 'The custom column has been removed',
      color: 'green',
    });
  }, [cohortId]);

  // Column config hook
  const columnConfigHook = useColumnConfig({
    preferenceKey: `participants_settings_${cohortId}`,
    defaultColumns: DEFAULT_COLUMNS,
    defaultPageSize: 25,
    customFieldDefinitions,
    onDeleteCustomField: handleDeleteCustomFieldApi,
  });

  const { sortConfig, page, setPage, pageSize, setPageSize } = columnConfigHook;

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, groupFilter, assignedFilter, personalityFilter, flaggedOnly, setPage]);

  // Fetch participants
  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await participantsApi.list(cohortId, {
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        groupId: groupFilter !== 'all' ? groupFilter : undefined,
        assignedStatus: assignedFilter !== 'all' ? assignedFilter : undefined,
        personalityType: personalityFilter !== 'all' ? personalityFilter : undefined,
        flaggedOnly: flaggedOnly || undefined,
        sortBy: sortConfig?.key,
        sortOrder: sortConfig?.order,
        page,
        limit: pageSize,
      });
      setParticipants(response.data);
      setSummary(response.summary);
      setTotalCount(response.pagination.total);

      if (response.customFields) {
        setCustomFieldDefinitions(response.customFields);
      }
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load participants',
        color: 'red',
      });
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, [cohortId, debouncedSearch, statusFilter, groupFilter, assignedFilter, personalityFilter, flaggedOnly, sortConfig, page, pageSize]);

  // Only fetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchParticipants();
    }
  }, [isActive, fetchParticipants]);

  // Fetch feedback survey configs once to build form URLs
  useEffect(() => {
    if (!isActive) return;
    feedbackSurveysApi.getConfigs(cohortId).then((configs) => {
      const urls: FormBaseUrls = { midpoint: null, endpoint: null, post_session: null };
      for (const cfg of configs) {
        if (cfg.form_url && cfg.target_audience !== 'coaches' && (cfg.survey_type === 'midpoint' || cfg.survey_type === 'endpoint' || cfg.survey_type === 'post_session')) {
          urls[cfg.survey_type] = cfg.form_url;
        }
      }
      setFormBaseUrls(urls);
    }).catch(() => {
      // Non-critical — columns will just show dashes
    });
  }, [cohortId, isActive]);

  // Fetch groups (all for card view, paginated for list view)
  const fetchGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      if (groupsDisplayMode === 'card') {
        // Card view: fetch all groups so coach assignment has the full picture
        const allGroups: GroupWithMembers[] = [];
        let currentOffset = 0;
        let hasMore = true;

        while (hasMore) {
          const result = await groupsApi.list(cohortId, {
            limit: 100,
            offset: currentOffset,
            search: groupsSearchValue || undefined,
          });
          allGroups.push(...result.data);
          hasMore = result.pagination.hasMore;
          currentOffset = result.pagination.offset + result.pagination.limit;
        }

        setCohortGroups(allGroups);
        setGroupsPagination({ total: allGroups.length, limit: allGroups.length, offset: 0, hasMore: false });
      } else {
        // List view: paginated fetch
        const result = await groupsApi.list(cohortId, {
          limit: GROUPS_PAGE_SIZE,
          offset: 0,
          search: groupsSearchValue || undefined,
        });
        setCohortGroups(result.data);
        setGroupsPagination(result.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load groups',
        color: 'red',
      });
    } finally {
      setLoadingGroups(false);
    }
  }, [cohortId, groupsSearchValue, groupsDisplayMode]);

  // Load more groups (appends to existing list)
  const loadMoreGroups = useCallback(async () => {
    if (!groupsPagination?.hasMore || loadingMoreGroups) return;
    setLoadingMoreGroups(true);
    try {
      const nextOffset = groupsPagination.offset + groupsPagination.limit;
      const result = await groupsApi.list(cohortId, {
        limit: GROUPS_PAGE_SIZE,
        offset: nextOffset,
        search: groupsSearchValue || undefined,
      });
      setCohortGroups((prev) => [...prev, ...result.data]);
      setGroupsPagination(result.pagination);
    } catch (error) {
      console.error('Failed to load more groups:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load more groups',
        color: 'red',
      });
    } finally {
      setLoadingMoreGroups(false);
    }
  }, [cohortId, groupsSearchValue, groupsPagination, loadingMoreGroups]);

  // Fetch ALL participants (paginating through all pages) for groups view demographics lookup
  const fetchAllParticipantsForGroups = useCallback(async () => {
    try {
      const allParticipants: Participant[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await participantsApi.list(cohortId, {
          limit: 100,
          page: currentPage,
        });
        allParticipants.push(...response.data);
        totalPages = response.pagination.totalPages;
        currentPage++;
      } while (currentPage <= totalPages);

      setAllParticipantsForGroups(allParticipants);
    } catch (error) {
      console.error('Failed to fetch all participants for groups:', error);
    }
  }, [cohortId]);

  // Fetch groups when tab is active
  useEffect(() => {
    if (isActive) {
      fetchGroups();
    }
  }, [isActive, fetchGroups]);

  // Fetch all participants when view mode changes to groups
  useEffect(() => {
    if (isActive && viewMode === 'groups') {
      fetchAllParticipantsForGroups();
    }
  }, [isActive, viewMode, fetchAllParticipantsForGroups]);

  // Apply client-side intake filter
  const filteredParticipants = useMemo(() => {
    if (intakeFilter === 'all') return participants;

    return participants.filter((p) => {
      if (intakeFilter === 'completed') {
        return p.intake_completed_at !== null;
      }
      if (intakeFilter === 'pending') {
        return p.intake_form_sent_at !== null && p.intake_completed_at === null;
      }
      if (intakeFilter === 'not_sent') {
        return p.intake_form_sent_at === null;
      }
      return true;
    });
  }, [participants, intakeFilter]);

  // Derived filter option lists — use cohortGroups (has id + name), sorted numerically
  const groupOptions = useMemo(
    () =>
      cohortGroups
        .slice()
        .sort((a, b) => {
          const numA = parseInt(a.name.replace(/\D/g, ''), 10);
          const numB = parseInt(b.name.replace(/\D/g, ''), 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.name.localeCompare(b.name, undefined, { numeric: true });
        })
        .map((g) => ({ value: g.id, label: g.name })),
    [cohortGroups],
  );

  const personalityTypes = useMemo(() => {
    const types = new Set<string>();
    participants.forEach((p) => {
      if (p.personality_type) {
        types.add(p.personality_type);
      }
    });
    return Array.from(types).sort();
  }, [participants]);

  // Summary stats
  const displaySummary = {
    total: summary?.total ?? 0,
    unassigned: summary?.unassigned ?? 0,
    avgAttendance: summary?.avg_attendance ?? null,
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters = !!(debouncedSearch || statusFilter !== 'all' || groupFilter !== 'all' || assignedFilter !== 'all' || personalityFilter !== 'all' || intakeFilter !== 'all' || flaggedOnly);

  const clearFilters = useCallback(() => {
    setSearchValue('');
    setStatusFilter('all');
    setGroupFilter('all');
    setAssignedFilter('all');
    setPersonalityFilter('all');
    setIntakeFilter('all');
    setFlaggedOnly(false);
    setPage(1);
  }, [setPage]);

  return {
    participants,
    filteredParticipants,
    summary,
    displaySummary,
    totalCount,
    totalPages,
    loading,
    refetching: loading && initialLoaded,
    customFieldDefinitions,
    cohortGroups,
    loadingGroups,
    loadingMoreGroups,
    groupsPagination,
    loadMoreGroups,
    allParticipantsForGroups,
    groupOptions,
    personalityTypes,
    filters: {
      searchValue,
      setSearchValue,
      debouncedSearch,
      statusFilter,
      setStatusFilter,
      groupFilter,
      setGroupFilter,
      assignedFilter,
      setAssignedFilter,
      personalityFilter,
      setPersonalityFilter,
      intakeFilter,
      setIntakeFilter,
      flaggedOnly,
      setFlaggedOnly,
      hasActiveFilters,
      clearFilters,
    },
    columnConfig: columnConfigHook,
    sortConfig,
    formBaseUrls,
    page,
    setPage,
    pageSize,
    setPageSize,
    fetchParticipants,
    fetchGroups,
    fetchAllParticipantsForGroups,
  };
}
